import { useTranslation } from 'react-i18next'
import { ArrowIcon } from '@/src/renderer/components/icons/ArrowIcon'
import { ArrowLeftIcon } from '@/src/renderer/components/icons/ArrowLeftIcon'
import { AddIcon } from '@/src/renderer/components/icons/AddIcon'
import { CommandIcon } from '@/src/renderer/components/icons/CommandIcon'
import type { ButtonShortcut, ButtonVariant } from './Button.types'
import './ShortcutBadge.scss'

// Single Shortcut Key Component
interface ShortcutKeyProps {
  shortcut: ButtonShortcut
}

const ShortcutKey = ({ shortcut }: ShortcutKeyProps) => {
  const renderKey = () => {
    switch (shortcut.variant) {
      case 'character':
        return (
          <span className="button__shortcut-key button__shortcut-key--character">
            {shortcut.text || ''}
          </span>
        )
      case 'return':
        return (
          <span className="button__shortcut-key button__shortcut-key--icon">
            <ArrowIcon />
          </span>
        )
      case 'add':
        return (
          <span className="button__shortcut-key button__shortcut-key--icon">
            <AddIcon />
          </span>
        )
      case 'back':
        return (
          <span className="button__shortcut-key button__shortcut-key--icon">
            <ArrowLeftIcon />
          </span>
        )
      default:
        return null
    }
  }

  return renderKey()
}

// ShortcutBadge Component
interface ShortcutBadgeProps {
  shortcut: ButtonShortcut | ButtonShortcut[] | undefined
  variant: ButtonVariant | 'beige'
}

export const ShortcutBadge = ({ shortcut, variant }: ShortcutBadgeProps) => {
  const { i18n } = useTranslation()

  if (!shortcut) return null

  const onDark = variant === 'primary' || variant === 'destructive'

  let badgeClass = `button__shortcut-badge`
  if (variant === 'beige') {
    badgeClass += ' button__shortcut-badge--beige'
  } else {
    badgeClass += ` button__shortcut-badge--${onDark ? 'on-dark' : 'on-light'}`
  }

  // Handle array of shortcuts
  if (Array.isArray(shortcut)) {
    return (
      <div className={badgeClass}>
        {shortcut.map((s, index) => (
          <ShortcutKey key={index} shortcut={s} />
        ))}
      </div>
    )
  }

  // Handle single shortcut with optional modifier
  const ctrlText = i18n.language === 'de' ? 'Strg' : 'Ctrl'

  return (
    <div className={badgeClass}>
      {shortcut.cmd && (
        <span className="button__shortcut-key button__shortcut-key--modifier button__shortcut-key--icon">
          <CommandIcon />
        </span>
      )}
      {!shortcut.cmd && shortcut.ctrl && (
        <span className="button__shortcut-key button__shortcut-key--modifier">{ctrlText}</span>
      )}
      <ShortcutKey shortcut={shortcut} />
    </div>
  )
}
